import type { NeighborhoodFeatureCollection } from "../types/CommunityImpact";

const MEMPHIS_NEIGHBORHOODS_QUERY_URL =
  "https://maps.memphistn.gov/mapping/rest/services/Basemaps/Common_Services_PROD/MapServer/35/query?where=1%3D1&outFields=OBJECTID,NHOOD&returnGeometry=true&outSR=4326&f=geojson";

const targetNeighborhoodNames = [
  "Binghampton",
  "Downtown Saginaw",
  "Frayser",
  "Orange Mound",
  "South Saginaw",
];

const normalizeNeighborhoodName = (value?: string) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const targetNeighborhoodNameSet = new Set(
  targetNeighborhoodNames.map(normalizeNeighborhoodName),
);

const neighborhoodNameAliases: Record<string, string> = {
  downtown: "Downtown Saginaw",
};

const targetNeighborhoodIds: Record<string, string> = {
  binghampton: "3",
  "downtown memphis": "2",
  frayser: "4",
  "orange mound": "1",
  "south memphis": "5",
};

export const fetchMemphisNeighborhoodPolygons =
  async (): Promise<NeighborhoodFeatureCollection> => {
    const response = await fetch(MEMPHIS_NEIGHBORHOODS_QUERY_URL);

    if (!response.ok) {
      throw new Error("Failed to fetch Saginaw neighborhood polygons");
    }

    const data = await response.json();
    const features = (data?.features || [])
      .map((feature: any) => {
        const sourceName = feature?.properties?.NHOOD || "";
        const normalizedName = normalizeNeighborhoodName(sourceName);
        const mappedName =
          neighborhoodNameAliases[normalizedName] || sourceName;

        return {
          ...feature,
          properties: {
            id:
              targetNeighborhoodIds[normalizeNeighborhoodName(mappedName)] ||
              String(feature?.properties?.OBJECTID || mappedName),
            name: mappedName,
          },
        };
      })
      .filter((feature: any) =>
        targetNeighborhoodNameSet.has(
          normalizeNeighborhoodName(feature?.properties?.name),
        ),
      );

    if (!features.length) {
      throw new Error("No matching Saginaw neighborhoods were returned");
    }

    return {
      type: "FeatureCollection",
      features,
    };
  };
export const communityImpactNeighborhoods: NeighborhoodFeatureCollection = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {
        id: "1",
        name: "Adams Boulevard",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.018, 43.425],
            [-84.004, 43.423],
            [-83.992, 43.428],
            [-83.991, 43.439],
            [-84.002, 43.444],
            [-84.016, 43.44],
            [-84.018, 43.425],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "2",
        name: "Butman-Fish",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.006, 43.41],
            [-83.99, 43.408],
            [-83.981, 43.416],
            [-83.984, 43.427],
            [-83.998, 43.431],
            [-84.008, 43.423],
            [-84.006, 43.41],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "3",
        name: "Cathedral District",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.956, 43.407],
            [-83.943, 43.405],
            [-83.934, 43.411],
            [-83.936, 43.421],
            [-83.947, 43.425],
            [-83.957, 43.418],
            [-83.956, 43.407],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "4",
        name: "Coulter",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.961, 43.338],
            [-83.947, 43.336],
            [-83.937, 43.342],
            [-83.939, 43.353],
            [-83.951, 43.358],
            [-83.962, 43.35],
            [-83.961, 43.338],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "5",
        name: "Covenant",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.985, 43.428],
            [-83.971, 43.426],
            [-83.961, 43.432],
            [-83.963, 43.442],
            [-83.975, 43.447],
            [-83.986, 43.44],
            [-83.985, 43.428],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "6",
        name: "Downtown",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.952, 43.425],
            [-83.94, 43.423],
            [-83.931, 43.428],
            [-83.932, 43.438],
            [-83.942, 43.443],
            [-83.952, 43.437],
            [-83.952, 43.425],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "7",
        name: "Fairground District",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.932, 43.378],
            [-83.919, 43.376],
            [-83.909, 43.382],
            [-83.911, 43.393],
            [-83.922, 43.398],
            [-83.933, 43.39],
            [-83.932, 43.378],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "8",
        name: "Heritage Square",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.002, 43.399],
            [-83.989, 43.397],
            [-83.979, 43.403],
            [-83.981, 43.413],
            [-83.992, 43.418],
            [-84.003, 43.411],
            [-84.002, 43.399],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "9",
        name: "Houghton-Jones",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.928, 43.42],
            [-83.915, 43.418],
            [-83.905, 43.424],
            [-83.907, 43.434],
            [-83.918, 43.439],
            [-83.929, 43.432],
            [-83.928, 43.42],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "10",
        name: "Northmoor",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.983, 43.442],
            [-83.969, 43.44],
            [-83.958, 43.446],
            [-83.96, 43.457],
            [-83.972, 43.462],
            [-83.984, 43.455],
            [-83.983, 43.442],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "11",
        name: "Northeast",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.92, 43.442],
            [-83.906, 43.44],
            [-83.895, 43.446],
            [-83.897, 43.457],
            [-83.909, 43.462],
            [-83.921, 43.455],
            [-83.92, 43.442],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "12",
        name: "Northwest",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.003, 43.414],
            [-83.989, 43.412],
            [-83.978, 43.418],
            [-83.98, 43.429],
            [-83.992, 43.434],
            [-84.004, 43.427],
            [-84.003, 43.414],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "13",
        name: "Old Town",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.986, 43.401],
            [-83.973, 43.399],
            [-83.963, 43.405],
            [-83.965, 43.415],
            [-83.976, 43.42],
            [-83.987, 43.413],
            [-83.986, 43.401],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "14",
        name: "Sherwill",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.948, 43.354],
            [-83.935, 43.352],
            [-83.925, 43.358],
            [-83.927, 43.368],
            [-83.938, 43.373],
            [-83.949, 43.366],
            [-83.948, 43.354],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "15",
        name: "Southeast",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.978, 43.367],
            [-83.965, 43.365],
            [-83.955, 43.371],
            [-83.957, 43.382],
            [-83.968, 43.387],
            [-83.979, 43.379],
            [-83.978, 43.367],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "16",
        name: "Southwest",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.014, 43.37],
            [-84.0, 43.368],
            [-83.989, 43.374],
            [-83.991, 43.385],
            [-84.003, 43.39],
            [-84.015, 43.383],
            [-84.014, 43.37],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "17",
        name: "St. Stephen's / Carman Park",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-84.025, 43.41],
            [-84.011, 43.408],
            [-84.0, 43.414],
            [-84.002, 43.425],
            [-84.014, 43.43],
            [-84.026, 43.423],
            [-84.025, 43.41],
          ],
        ],
      },
    },
    {
      type: "Feature",
      properties: {
        id: "18",
        name: "Unity in the Community",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.917, 43.403],
            [-83.904, 43.401],
            [-83.894, 43.407],
            [-83.896, 43.417],
            [-83.907, 43.422],
            [-83.918, 43.415],
            [-83.917, 43.403],
          ],
        ],
      },
    },
  ],
};
